import { Middleware } from '@nuxt/types'
import { Route } from 'vue-router'
import { isSitecoreRequest } from '~/utils/isSitecoreRequest'
import isEmptyObject from '~/helpers/is-empty-object'

type Options = { features?: string[] }

interface Component {
  options: Options,
  _Ctor: Component[]
}

const getComponentFlags = (component: Component) => {
  if (process.client) {
    return component.options.features || []
  }

  // Pinched from https://github.com/SohoHouse/nuxt-oauth/blob/master/lib/plugin.js#L33
  const { _Ctor: ctor } = component

  const flags = Object.values(ctor).reduce<string[]>((currentFlags, value) => {
    if (!value.options || !value.options.features) {
      return currentFlags
    }

    return [...currentFlags, ...value.options.features]
  }, [])

  return flags
}

const getFlags = ({ matched }: Route) => {
  const flags = matched.reduce<string[]>((currentFlags, { components }) => {
    if (!components) {
      return currentFlags
    }

    const matchedFlags = Object.values(components).reduce<string[]>((currentComponentFlags, component) => {
      const componentFlags = getComponentFlags(component as Component)

      return [...currentComponentFlags, ...componentFlags]
    }, [])

    return [...currentFlags, ...matchedFlags]
  }, [])

  return flags
}

const featureFlagging: Middleware = async ({ app, req, store, route, error }) => {
  if (isSitecoreRequest(req)) {
    return
  }

  const flags = getFlags(route)

  if (!flags.length) {
    return
  }

  const hasToken = !!store.state.oauth.accessToken

  // The page has flags, needs to be an authenticated user
  if (!hasToken) {
    // This will cause a redirect loop for public users if you add feature flags to the index page
    // Don't do that

    return error({ statusCode: 404 })
  }

  // Get the account if it's not already been fetched - helpful if this is running serverside
  if (isEmptyObject(store.getters['user/account/account'])) {
    await store.dispatch('user/fetchUser')
  }

  const isFeatureEnabled = store.getters['user/account/isFeatureEnabled']

  if (flags.every((flag: string) => isFeatureEnabled(flag))) {
    return
  }

  error({ statusCode: 404 })
}

export default featureFlagging
